/**
 * Photo Component
 *
 * An individual photo component, used in the Photo Galleries
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import SVGLoader from '../svg/loader'
import { getMediaUrl } from '../../utils/helpers'

const Photo = ({ photo, onClick, selected = false, overlayLabel = null }) => {
  const [src, setSrc] = useState(() => getMediaUrl(photo.image))
  const [loading, setLoading] = useState(true)
  const [blockHeight, setBlockHeight] = useState(0)
  const containerRef = useRef(null)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    const updateHeight = () => {
      if (containerRef.current) {
        setBlockHeight(containerRef.current.offsetWidth)
      }
    }
    updateHeight()

    if (typeof ResizeObserver !== `undefined`) {
      const observer = new ResizeObserver(updateHeight)
      observer.observe(el)
      return () => observer.disconnect()
    }

    window.addEventListener(`resize`, updateHeight)
    return () => window.removeEventListener(`resize`, updateHeight)
  }, [])

  useEffect(() => {
    const imageLoader = new Image()
    imageLoader.src = getMediaUrl(photo.image)
    imageLoader.onload = () => {
      setSrc(getMediaUrl(photo.image))
      setLoading(false)
    }
    imageLoader.onerror = () => {
      setLoading(false)
    }
  }, [photo.image])

  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick(photo)
    }
  }

  const thumbHeight = blockHeight || undefined

  return (
    <div className={`photo-container`} ref={containerRef}>
      <div className={`photo-thumb ${selected ? `selected` : ``}`} style={{ height: thumbHeight }}>
        <button
          type="button"
          onClick={handleClick}
          className={`btn btn-link btn-photo px-0 py-0`}
          title="Select photo">
          {loading ? (
            <div className={`d-flex align-items-center h-100 justify-content-center text-primary`}>
              <SVGLoader className={`text-primary`} width={20} height={20} />
            </div>
          ) : (
            <div className={`img-preview px-0`} style={{ backgroundImage: `url(${src})` }}></div>
          )}
          <p className={`mb-0 sr-only`}>{photo.description}</p>
          <small className={`sr-only`}>Uploaded by {photo.author}</small>
        </button>
        {overlayLabel && (
          <span className="photo-overlay-label" aria-hidden="true">
            {overlayLabel}
          </span>
        )}
      </div>
    </div>
  )
}

Photo.propTypes = {
  photo: PropTypes.object,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  overlayLabel: PropTypes.string,
}

export default Photo
